/**
 * The vault: session state, key material, and the decrypted library.
 *
 * Two rules govern this file.
 *
 * 1. The master key and its subkeys live in this closure and nowhere else — no
 *    localStorage, no sessionStorage, no module-level cache that survives a
 *    reload. Closing the tab locks the vault, which is the intent.
 * 2. Nothing plaintext is ever handed back to the server. Every write goes
 *    through `seal` on the way out.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { ConflictError, api, decodePayload } from './api';
import { cachedUuids, clearCache, deleteBlobs, storageEstimate } from './blobCache';
import { clearOfflineCache } from './offlineCache';
import type { StorageEstimate } from './blobCache';
import {
  b64decode,
  deriveMasterKey,
  deriveVaultKeys,
  fromUtf8,
  seal,
  unseal,
  utf8,
  zero,
  type VaultKeys,
} from './crypto';
import {
  PRELOAD_ALL,
  emptySettings,
  isSlideshow,
  pickVariant,
  sortFolders,
  sortVideos,
  type Item,
  type PlaylistPayload,
  type RawItem,
  type SettingsPayload,
  type VideoPayload,
} from './types';

export type VaultStatus = 'checking' | 'signed-out' | 'locked' | 'unlocking' | 'unlocked';

export class WrongPassphrase extends Error {
  constructor() {
    super('That passphrase does not open this vault.');
    this.name = 'WrongPassphrase';
  }
}

interface VaultState {
  status: VaultStatus;
  username: string | null;
  /** Non-null only while unlocking, 0..1. */
  progress: number | null;
  videos: Item<VideoPayload>[];
  playlists: Item<PlaylistPayload>[];
  settings: SettingsPayload;
  /** Blobs that failed to decrypt — surfaced rather than silently dropped. */
  broken: string[];
  error: string | null;
}

interface VaultApi extends VaultState {
  keys: VaultKeys | null;
  signIn(username: string, password: string): Promise<void>;
  unlock(passphrase: string): Promise<void>;
  lock(): void;
  signOut(): Promise<void>;
  reload(): Promise<void>;
  createFolder(name: string): Promise<void>;
  renameFolder(uuid: string, name: string): Promise<void>;
  /** Delete a folder. Its videos become unfiled; none are touched. */
  deleteFolder(uuid: string): Promise<void>;
  /** Move a video into a folder, or out of all of them with null. */
  moveVideo(video: Item<VideoPayload>, folder: string | null): Promise<void>;
  /** Shift a folder up (-1) or down (+1) in the sidebar. */
  moveFolder(uuid: string, delta: number): Promise<void>;
  /** Shift a video up (-1) or down (+1) among its folder-mates. */
  reorderVideo(video: Item<VideoPayload>, delta: number): Promise<void>;
  saveSettings(next: SettingsPayload): Promise<void>;
  /** Decrypt a blob to an object URL, cached. Used for thumbnails. */
  thumbUrl(video: Item<VideoPayload>): Promise<string | null>;
  /** One decrypted slide as an object URL. The caller must revoke it. */
  slideUrl(blob: string): Promise<string | null>;
  /** Warm the blob cache with one rung of a video. */
  preload(video: Item<VideoPayload>, height: number): void;
  cancelPreload(uuid: string): void;
  preloads: Record<string, PreloadState>;
  /** Every blob uuid currently in the cache. The source of truth for "loaded". */
  cached: Set<string>;
  /** The blobs a given video needs at a given quality. Resolved once, then reused. */
  planFor(video: Item<VideoPayload>, height: number): Promise<string[]>;
  /**
   * Items the server holds, or null while unknown. Zero means the next
   * passphrase entered *creates* the vault key rather than checking against
   * one, which the UI must say out loud.
   */
  itemCount: number | null;
  /** How much disk the browser is letting this origin use, and how much is used. */
  storage: StorageEstimate | null;
  /** Drop every cached blob. */
  clearAllCached(): Promise<void>;
  refreshCache(): Promise<void>;
  evict(video: Item<VideoPayload>): Promise<void>;
  /** Per-rung cache coverage, for showing which resolution is downloaded. */
  cachedRungs(video: Item<VideoPayload>): Promise<RungCoverage[]>;
  /** Ordered segments per rung, resolved from the encrypted variant manifests. */
  rungSegments(video: Item<VideoPayload>, cachedOnly?: boolean): Promise<RungSegments[]>;
  /**
   * Variant blob uuids in hls.js level-index order, read from the master.
   *
   * hls.js orders levels by ascending BANDWIDTH. Inferring the index from a
   * rung's height instead assumes bitrate and resolution agree, which they
   * need not — `--min-bitrate` alone can flatten several rungs to the same
   * bitrate and leave the tie broken by manifest order.
   */
  levelOrder(video: Item<VideoPayload>): Promise<string[]>;
  /** How much of the timeline is cached, at whatever quality. */
  coverageOf(video: Item<VideoPayload>): Promise<Coverage>;
}

export interface RungCoverage {
  name: string;
  have: number;
  total: number;
}

/** One rung's ordered segment list, so copies can be compared by time index. */
export interface RungSegments {
  name: string;
  height: number;
  manifestBlob: string;
  /** Segment blob uuids in playback order. Index i is the same instant in
   *  every rung — the encoder pins keyframes so all rungs cut together. */
  segments: string[];
}

/** Timeline coverage: how much of the video is on disk, and at what quality. */
export interface Coverage {
  /** Fraction of time indices with at least one cached copy, 0..1. */
  fraction: number;
  indices: number;
  covered: number;
  /** Rung names present, best first. */
  rungs: string[];
  /**
   * The rung playback will actually open at: the best one whose *first*
   * segment is cached, or null if the opening moment is not cached at all.
   *
   * Distinct from `rungs[0]`, which is merely the best quality appearing
   * anywhere. Reporting that as the headline overclaimed — a video could show
   * "1080p" with one 1080p segment in the middle and a 240p opening.
   */
  startRung: string | null;
  /** Cached segments per rung, best first, for diagnosis. */
  perRung: { name: string; have: number; total: number }[];
}

export interface PreloadState {
  done: number;
  total: number;
  bytes: number;
  running: boolean;
  error: string | null;
}

/**
 * How often to ask the server whether anything changed.
 *
 * The check is a few numbers, not the library, so this is cheap — but it only
 * runs while the tab is visible, and a focus event triggers one immediately.
 * The common case is a CLI upload finishing while the tab sits in the
 * background, and that is caught the moment it is looked at.
 */
const POLL_MS = 30_000;

/**
 * Blobs fetched at once while preloading.
 *
 * Four rather than one because a slow link is usually latency-bound as much as
 * bandwidth-bound, and four rather than forty because the point is to warm the
 * cache in the background, not to starve playback happening at the same time.
 */
const PRELOAD_CONCURRENCY = 4;

const VaultContext = createContext<VaultApi | null>(null);

export function useVault(): VaultApi {
  const ctx = useContext(VaultContext);
  if (!ctx) throw new Error('useVault must be used inside <VaultProvider>');
  return ctx;
}

const INITIAL: VaultState = {
  status: 'checking',
  username: null,
  progress: null,
  videos: [],
  playlists: [],
  settings: emptySettings(),
  broken: [],
  error: null,
};

export function VaultProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<VaultState>(INITIAL);
  const keysRef = useRef<VaultKeys | null>(null);
  const saltRef = useRef<Uint8Array | null>(null);
  const settingsItemRef = useRef<{ uuid: string; version: number } | null>(null);
  const thumbCache = useRef(new Map<string, string>());
  const preloadControllers = useRef(new Map<string, AbortController>());
  const [preloads, setPreloads] = useState<Record<string, PreloadState>>({});
  const [cached, setCached] = useState<Set<string>>(new Set());
  const [itemCount, setItemCount] = useState<number | null>(null);
  const [storage, setStorage] = useState<StorageEstimate | null>(null);
  // Resolving a plan means decrypting a variant manifest, so memoise per
  // (video, quality) rather than redoing it on every render of every row.
  const planCache = useRef(new Map<string, Promise<string[]>>());
  /**
   * Decrypted items keyed by `uuid:version`, so a reload that changes nothing
   * hands back the *same objects*.
   *
   * Identity matters here: a fresh object for an unchanged video propagates
   * into PlayerCell's effect dependencies and tears down a running hls.js
   * instance. A background poll, or saving an unrelated setting, would
   * otherwise restart every playing video.
   */
  const itemIdentity = useRef(new Map<string, Item<unknown>>());

  // Is there already a session cookie? Decides sign-in screen vs unlock screen.
  useEffect(() => {
    let live = true;
    api
      .me()
      .then((me) => {
        if (!live) return;
        saltRef.current = b64decode(me.salt_b64);
        setState((s) => ({ ...s, status: 'locked', username: me.username }));
      })
      .catch(() => {
        if (live) setState((s) => ({ ...s, status: 'signed-out' }));
      });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    if (state.status !== 'locked') return;
    let live = true;
    api
      .usage()
      .then((u) => {
        if (live) setItemCount(u.item_count);
      })
      .catch(() => {
        if (live) setItemCount(null);
      });
    return () => {
      live = false;
    };
  }, [state.status]);

  const revokeThumbs = useCallback(() => {
    for (const url of thumbCache.current.values()) URL.revokeObjectURL(url);
    thumbCache.current.clear();
  }, []);

  const signIn = useCallback(async (username: string, password: string) => {
    const me = await api.login(username, password);
    saltRef.current = b64decode(me.salt_b64);
    setState((s) => ({ ...s, status: 'locked', username: me.username, error: null }));
  }, []);

  const lock = useCallback(() => {
    const keys = keysRef.current;
    if (keys) {
      zero(keys.metaRaw);
      zero(keys.wrapRaw);
    }
    keysRef.current = null;
    settingsItemRef.current = null;
    revokeThumbs();
    setState((s) => ({
      ...INITIAL,
      status: s.username ? 'locked' : 'signed-out',
      username: s.username,
    }));
  }, [revokeThumbs]);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      keysRef.current = null;
      saltRef.current = null;
      revokeThumbs();
      // Signing out should not leave the library readable to the next person
      // to open the tab, offline or otherwise.
      await clearOfflineCache();
      setState({ ...INITIAL, status: 'signed-out' });
    }
  }, [revokeThumbs]);

  /** Decrypt every item, tolerating individual failures. */
  const hydrate = useCallback(async (keys: VaultKeys, raw: RawItem[]) => {
    const videos: Item<VideoPayload>[] = [];
    const playlists: Item<PlaylistPayload>[] = [];
    const broken: string[] = [];
    let settings = emptySettings();
    let settingsItem: { uuid: string; version: number } | null = null;

    for (const item of raw) {
      let payload: unknown;
      try {
        payload = JSON.parse(fromUtf8(await unseal(keys.meta, decodePayload(item))));
      } catch {
        // One corrupt row must not blank the whole library.
        broken.push(item.uuid);
        continue;
      }
      const decrypted = {
        uuid: item.uuid,
        kind: item.kind,
        version: item.version,
        updatedAt: item.updated_at,
      };
      const identityKey = `${item.uuid}:${item.version}`;
      const previous = itemIdentity.current.get(identityKey);

      if (item.kind === 'video') {
        const next =
          (previous as Item<VideoPayload>) ?? { ...decrypted, payload: payload as VideoPayload };
        itemIdentity.current.set(identityKey, next);
        videos.push(next);
      } else if (item.kind === 'playlist') {
        const next =
          (previous as Item<PlaylistPayload>) ??
          { ...decrypted, payload: payload as PlaylistPayload };
        itemIdentity.current.set(identityKey, next);
        playlists.push(next);
      } else if (item.kind === 'settings' && !settingsItem) {
        settings = { ...emptySettings(), ...(payload as SettingsPayload) };
        settingsItem = { uuid: item.uuid, version: item.version };
      }
    }

    // Drop identities for versions no longer present, so an edited or deleted
    // item cannot pin a stale payload in memory forever.
    const liveKeys = new Set(raw.map((i) => `${i.uuid}:${i.version}`));
    for (const key of itemIdentity.current.keys()) {
      if (!liveKeys.has(key)) itemIdentity.current.delete(key);
    }

    const orderedVideos = sortVideos(videos);
    videos.length = 0;
    videos.push(...orderedVideos);
    const orderedPlaylists = sortFolders(playlists);
    playlists.length = 0;
    playlists.push(...orderedPlaylists);
    return { videos, playlists, settings, settingsItem, broken };
  }, []);

  const unlock = useCallback(
    async (passphrase: string) => {
      const salt = saltRef.current;
      if (!salt) throw new Error('No salt — sign in first.');

      setState((s) => ({ ...s, status: 'unlocking', progress: 0, error: null }));
      let keys: VaultKeys;
      try {
        const master = await deriveMasterKey(passphrase, salt, (p) =>
          setState((s) => (s.status === 'unlocking' ? { ...s, progress: p } : s)),
        );
        keys = await deriveVaultKeys(master);
        zero(master);
      } catch (err) {
        setState((s) => ({ ...s, status: 'locked', progress: null, error: String(err) }));
        throw err;
      }

      const raw = await api.listItems();
      const { videos, playlists, settings, settingsItem, broken } = await hydrate(keys, raw);

      // Wrong-passphrase detection. An existing vault has at least one item we
      // must be able to open; if every single one failed, the passphrase — not
      // the data — is what is wrong.
      if (raw.length > 0 && broken.length === raw.length) {
        zero(keys.metaRaw);
        zero(keys.wrapRaw);
        setState((s) => ({ ...s, status: 'locked', progress: null, error: null }));
        throw new WrongPassphrase();
      }

      keysRef.current = keys;
      settingsItemRef.current = settingsItem;
      let writeError: string | null = null;

      // First unlock of an empty vault: write the canary so the next unlock has
      // something to check against.
      if (!settingsItem) {
        try {
          const created = await api.createItem(
            'settings',
            await seal(keys.meta, utf8(JSON.stringify(settings))),
          );
          settingsItemRef.current = { uuid: created.uuid, version: created.version };
        } catch (err) {
          // Do not swallow this. A silent failure here is indistinguishable
          // from working, and it hid a CSRF bug that broke every write in the
          // app while reads kept succeeding.
          console.error('peepshow: could not write the settings item', err);
          writeError = err instanceof Error ? err.message : String(err);
        }
      }

      setState({
        status: 'unlocked',
        username: state.username,
        progress: null,
        videos,
        playlists,
        settings,
        broken,
        error: writeError,
      });
    },
    [hydrate, state.username],
  );

  const reload = useCallback(async () => {
    const keys = keysRef.current;
    if (!keys) return;
    const raw = await api.listItems();
    const { videos, playlists, settings, settingsItem, broken } = await hydrate(keys, raw);
    if (settingsItem) settingsItemRef.current = settingsItem;
    setState((s) => ({ ...s, videos, playlists, settings, broken }));
  }, [hydrate]);

  const createFolder = useCallback(
    async (name: string) => {
      const keys = keysRef.current;
      if (!keys) throw new Error('Vault is locked.');
      const payload: PlaylistPayload = { name, created: new Date().toISOString() };
      await api.createItem('playlist', await seal(keys.meta, utf8(JSON.stringify(payload))));
      await reload();
    },
    [reload],
  );

  const renameFolder = useCallback(
    async (uuid: string, name: string) => {
      const keys = keysRef.current;
      const folder = state.playlists.find((p) => p.uuid === uuid);
      if (!keys || !folder) return;
      const payload: PlaylistPayload = { ...folder.payload, name };
      await api.patchItem(
        uuid,
        await seal(keys.meta, utf8(JSON.stringify(payload))),
        folder.version,
      );
      await reload();
    },
    [reload, state.playlists],
  );

  /**
   * Delete a folder without touching its videos.
   *
   * They keep pointing at an id that no longer resolves, which reads as
   * unfiled. Rewriting every member would be a PATCH per video — slow, and
   * each one a chance to fail halfway and leave the library inconsistent.
   */
  const deleteFolder = useCallback(
    async (uuid: string) => {
      await api.deleteItem(uuid);
      await reload();
    },
    [reload],
  );

  /**
   * Reorder by rewriting only the two folders that swap.
   *
   * Positions are normalised first if any are missing, which costs a write per
   * folder — but only once, on the first reorder of a vault that predates the
   * field.
   */
  const moveFolder = useCallback(
    async (uuid: string, delta: number) => {
      const keys = keysRef.current;
      if (!keys) throw new Error('Vault is locked.');
      const ordered = sortFolders(state.playlists);
      const from = ordered.findIndex((f) => f.uuid === uuid);
      const to = from + delta;
      if (from < 0 || to < 0 || to >= ordered.length) return;

      const write = async (folder: Item<PlaylistPayload>, order: number) => {
        const payload: PlaylistPayload = { ...folder.payload, order };
        await api.patchItem(
          folder.uuid,
          await seal(keys.meta, utf8(JSON.stringify(payload))),
          folder.version,
        );
      };

      if (ordered.some((f) => f.payload.order === undefined)) {
        // Give everything a position, with the swap already applied.
        const swapped = ordered.slice();
        [swapped[from], swapped[to]] = [swapped[to], swapped[from]];
        for (const [index, folder] of swapped.entries()) await write(folder, index);
      } else {
        await write(ordered[from], ordered[to].payload.order!);
        await write(ordered[to], ordered[from].payload.order!);
      }
      await reload();
    },
    [reload, state.playlists],
  );

  /**
   * Reorder within a folder.
   *
   * Siblings are the videos sharing this one's folder — including the unfiled
   * pile, which is a folder in every way that matters here. Only the two that
   * swap are written, after normalising positions once if any are missing.
   */
  const reorderVideo = useCallback(
    async (video: Item<VideoPayload>, delta: number) => {
      const keys = keysRef.current;
      if (!keys) throw new Error('Vault is locked.');
      const folder = video.payload.folder ?? null;
      const known = new Set(state.playlists.map((p) => p.uuid));
      const siblings = sortVideos(
        state.videos.filter((v) => {
          const f = v.payload.folder && known.has(v.payload.folder) ? v.payload.folder : null;
          return f === (folder && known.has(folder) ? folder : null);
        }),
      );

      const from = siblings.findIndex((v) => v.uuid === video.uuid);
      const to = from + delta;
      if (from < 0 || to < 0 || to >= siblings.length) return;

      const write = async (item: Item<VideoPayload>, order: number) => {
        const payload: VideoPayload = { ...item.payload, order };
        await api.patchItem(
          item.uuid,
          await seal(keys.meta, utf8(JSON.stringify(payload))),
          item.version,
        );
      };

      if (siblings.some((v) => v.payload.order === undefined)) {
        const swapped = siblings.slice();
        [swapped[from], swapped[to]] = [swapped[to], swapped[from]];
        for (const [index, item] of swapped.entries()) await write(item, index);
      } else {
        await write(siblings[from], siblings[to].payload.order!);
        await write(siblings[to], siblings[from].payload.order!);
      }
      await reload();
    },
    [reload, state.playlists, state.videos],
  );

  const moveVideo = useCallback(
    async (video: Item<VideoPayload>, folder: string | null) => {
      const keys = keysRef.current;
      if (!keys) throw new Error('Vault is locked.');
      const payload: VideoPayload = { ...video.payload, folder };
      const sealed = await seal(keys.meta, utf8(JSON.stringify(payload)));
      try {
        await api.patchItem(video.uuid, sealed, video.version);
      } catch (err) {
        // Another tab moved it first. Reload so the user sees where it went
        // rather than silently overwriting their other device's decision.
        if (!(err instanceof ConflictError)) throw err;
      }
      await reload();
    },
    [reload],
  );

  const saveSettings = useCallback(async (next: SettingsPayload) => {
    setState((s) => ({ ...s, settings: next }));
    const keys = keysRef.current;
    const ref = settingsItemRef.current;
    if (!keys) return;
    const sealed = await seal(keys.meta, utf8(JSON.stringify(next)));
    try {
      if (ref) {
        const updated = await api.patchItem(ref.uuid, sealed, ref.version);
        settingsItemRef.current = { uuid: updated.uuid, version: updated.version };
      } else {
        const created = await api.createItem('settings', sealed);
        settingsItemRef.current = { uuid: created.uuid, version: created.version };
      }
    } catch (err) {
      // Another tab moved first. Its layout wins; ours is only a preference.
      if (!(err instanceof ConflictError)) throw err;
    }
  }, []);

  const cancelPreload = useCallback((uuid: string) => {
    preloadControllers.current.get(uuid)?.abort();
    preloadControllers.current.delete(uuid);
    setPreloads((p) => {
      const current = p[uuid];
      return current ? { ...p, [uuid]: { ...current, running: false } } : p;
    });
  }, []);

  const refreshCache = useCallback(async () => {
    setCached(await cachedUuids());
    setStorage(await storageEstimate());
  }, []);

  const clearAllCached = useCallback(async () => {
    await clearCache();
    setCached(new Set());
    setStorage(await storageEstimate());
  }, []);


  // Read the cache once the library is known, so a video preloaded in an
  // earlier session shows as loaded rather than untouched.
  useEffect(() => {
    if (state.status === 'unlocked') void refreshCache();
  }, [state.status, state.videos, refreshCache]);


  /**
   * The blobs preload should fetch for one video at the chosen quality.
   *
   * Falls back to the full set when the target is 0, when the payload predates
   * per-rung metadata, or when the variant manifest cannot be read. Fetching
   * too much is wasteful; fetching too little would leave playback stalling on
   * a segment the user believed was already cached.
   */
  const blobsToWarm = useCallback(
    async (video: Item<VideoPayload>, height: number, signal: AbortSignal) => {
      const payload = video.payload;
      const all = payload.blobs ?? [];
      const variant = pickVariant(payload.variants, height);
      if (!variant) return all;

      // A slideshow names its blobs outright; there is no manifest to fetch and
      // nothing to parse. Match on rung name rather than on the representative
      // blob, which belongs to the first slide only.
      if (isSlideshow(payload)) {
        const wanted = (payload.slides ?? [])
          .map((slide) => slide.variants.find((v) => v.name === variant.name)?.blob)
          .filter((blob): blob is string => Boolean(blob));
        if (payload.thumb_blob) wanted.push(payload.thumb_blob);
        return wanted.length ? [...new Set(wanted)] : all;
      }

      let manifest: string;
      try {
        const keys = keysRef.current;
        if (!keys) return all;
        manifest = fromUtf8(await unseal(keys.meta, await api.getBlob(variant.blob, signal)));
      } catch {
        return all;
      }

      const segments = manifest
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.startsWith('peepshow://'))
        .map((line) => line.split('/').pop() ?? '')
        .filter(Boolean);
      if (segments.length === 0) return all;

      // The master and variant manifests are a few hundred bytes and the
      // player needs both before it can ask for a single segment.
      const wanted = [payload.master_blob ?? '', variant.blob, ...segments].filter(Boolean);
      if (payload.thumb_blob) wanted.push(payload.thumb_blob);
      return [...new Set(wanted)];
    },
    [],
  );

  const planFor = useCallback(
    (video: Item<VideoPayload>, height: number) => {
      const key = `${video.uuid}:${height}`;
      const existing = planCache.current.get(key);
      if (existing) return existing;
      const promise = blobsToWarm(video, height, new AbortController().signal).catch(
        () => video.payload.blobs ?? [],
      );
      planCache.current.set(key, promise);
      return promise;
    },
    [blobsToWarm],
  );



  /**
   * Ordered segment uuids for each rung, read from its variant manifest.
   *
   * Index i is the same instant in every rung: the encoder pins keyframes to a
   * fixed cadence so all rungs cut together, which is what makes copies at
   * different qualities directly comparable.
   *
   * `cachedOnly` avoids fetching manifests over the network for rungs that
   * cannot possibly have cached segments — the difference between free and one
   * request per rung per video when the library renders.
   */
  const rungSegments = useCallback(
    async (video: Item<VideoPayload>, cachedOnly = false): Promise<RungSegments[]> => {
      const keys = keysRef.current;
      if (!keys) return [];
      const variants = [...(video.payload.variants ?? [])].sort((a, b) => a.height - b.height);
      const out: RungSegments[] = [];

      // For a slideshow, slide N is the same picture in every rung exactly as
      // segment N is the same instant — so the reconciliation that keeps one
      // copy per index works unchanged, and coverage means what it says.
      if (isSlideshow(video.payload)) {
        for (const variant of variants) {
          const segments = (video.payload.slides ?? [])
            .map((slide) => slide.variants.find((v) => v.name === variant.name)?.blob)
            .filter((blob): blob is string => Boolean(blob));
          if (!segments.length) continue;
          if (cachedOnly && !segments.some((blob) => cached.has(blob))) continue;
          out.push({
            name: variant.name,
            height: variant.height,
            manifestBlob: variant.blob,
            segments,
          });
        }
        return out.sort((a, b) => b.height - a.height);
      }

      for (const variant of variants) {
        if (cachedOnly && !cached.has(variant.blob)) continue;
        const key = `segments:${variant.blob}`;
        let promise = planCache.current.get(key);
        if (!promise) {
          promise = (async () => {
            try {
              const text = fromUtf8(await unseal(keys.meta, await api.getBlob(variant.blob)));
              return text
                .split('\n')
                .map((line) => line.trim())
                .filter((line) => line.startsWith('peepshow://'))
                .map((line) => line.split('/').pop() ?? '')
                .filter(Boolean);
            } catch {
              return [];
            }
          })();
          planCache.current.set(key, promise);
        }
        const segments = await promise;
        if (segments.length) {
          out.push({
            name: variant.name,
            height: variant.height,
            manifestBlob: variant.blob,
            segments,
          });
        }
      }
      // Best first: every consumer wants the highest quality copy.
      return out.sort((a, b) => b.height - a.height);
    },
    [cached],
  );

  const levelOrder = useCallback(
    async (video: Item<VideoPayload>): Promise<string[]> => {
      const keys = keysRef.current;
      if (!keys || !video.payload.master_blob) return [];
      try {
        const master = fromUtf8(await unseal(keys.meta, await api.getBlob(video.payload.master_blob)));
        const lines = master.split('\n').map((l) => l.trim());
        const entries: { bandwidth: number; blob: string }[] = [];
        for (let i = 0; i < lines.length; i += 1) {
          if (!lines[i].startsWith('#EXT-X-STREAM-INF')) continue;
          const bandwidth = Number(/BANDWIDTH=(\d+)/.exec(lines[i])?.[1] ?? 0);
          const url = lines[i + 1] ?? '';
          if (!url.startsWith('peepshow://')) continue;
          entries.push({ bandwidth, blob: url.split('/').pop() ?? '' });
        }
        // Stable sort keeps manifest order for equal bandwidths, which is what
        // hls.js's own sort does with ties.
        return entries.sort((a, b) => a.bandwidth - b.bandwidth).map((e) => e.blob);
      } catch {
        return [];
      }
    },
    [],
  );

  const coverageOf = useCallback(
    async (video: Item<VideoPayload>): Promise<Coverage> => {
      const rungs = await rungSegments(video, true);
      if (rungs.length === 0) {
        return { fraction: 0, indices: 0, covered: 0, rungs: [], startRung: null, perRung: [] };
      }
      const indices = Math.max(...rungs.map((r) => r.segments.length));
      let covered = 0;
      const present = new Set<string>();
      for (let i = 0; i < indices; i += 1) {
        const best = rungs.find((r) => r.segments[i] && cached.has(r.segments[i]));
        if (best) {
          covered += 1;
          present.add(best.name);
        }
      }
      const startRung = rungs.find((r) => r.segments[0] && cached.has(r.segments[0]))?.name ?? null;
      return {
        fraction: indices ? covered / indices : 0,
        indices,
        covered,
        rungs: rungs.filter((r) => present.has(r.name)).map((r) => r.name),
        startRung,
        perRung: rungs.map((r) => ({
          name: r.name,
          have: r.segments.reduce((n, uuid) => n + (cached.has(uuid) ? 1 : 0), 0),
          total: r.segments.length,
        })),
      };
    },
    [cached, rungSegments],
  );

  /**
   * Keep the best cached copy of each moment, and drop the rest.
   *
   * Rungs are interchangeable per time index — the encoder cuts them at the
   * same instants — so holding 240p and 480p of the same six seconds is pure
   * duplication. For each index the highest-quality cached copy wins and the
   * others go.
   *
   * This replaces a whole-rung rule that could not express what actually
   * happens: watching pulls whatever ABR chose, minute to minute, so real
   * coverage is a patchwork rather than one clean rung. A manifest is kept
   * whenever any of its segments survive, since the player needs it to use
   * them.
   */
  const pruneRunning = useRef(false);
  const enforceSingleRung = useCallback(
    async (videos: Item<VideoPayload>[], present: Set<string>) => {
      if (pruneRunning.current) return;
      pruneRunning.current = true;
      try {
        const doomed: string[] = [];
        for (const video of videos) {
          // Never reconcile mid-preload: a rung still arriving would lose every
          // comparison against one already complete, and its segments would be
          // deleted as fast as they downloaded.
          if (preloadControllers.current.has(video.uuid)) continue;

          const rungs = await rungSegments(video, true); // best first
          if (rungs.length < 2) continue;

          const indices = Math.max(...rungs.map((r) => r.segments.length));
          const keepManifest = new Set<string>();
          for (let i = 0; i < indices; i += 1) {
            let kept = false;
            for (const rung of rungs) {
              const uuid = rung.segments[i];
              if (!uuid || !present.has(uuid)) continue;
              if (!kept) {
                kept = true;
                keepManifest.add(rung.manifestBlob);
              } else {
                doomed.push(uuid);
              }
            }
          }
          // A manifest whose every segment lost is now dead weight.
          for (const rung of rungs) {
            if (!keepManifest.has(rung.manifestBlob) && present.has(rung.manifestBlob)) {
              doomed.push(rung.manifestBlob);
            }
          }
        }
        if (doomed.length) {
          await deleteBlobs(new Set(doomed));
          setCached(await cachedUuids());
        }
      } finally {
        pruneRunning.current = false;
      }
    },
    [rungSegments],
  );

  // Reconcile after every cache change, so extra rungs cannot accumulate
  // however they got there.
  useEffect(() => {
    if (state.status !== 'unlocked' || cached.size === 0) return;
    void enforceSingleRung(state.videos, cached);
  }, [state.status, state.videos, cached, enforceSingleRung]);

  /**
   * How much of each rung is cached, for showing what has been downloaded.
   *
   * A rung whose own manifest is not cached cannot have been preloaded, so
   * that case short-circuits before resolving a plan. Without it, rendering
   * the library would fetch a manifest per rung per video just to discover
   * that nothing is there.
   */
  const cachedRungs = useCallback(
    async (video: Item<VideoPayload>) => {
      const variants = [...(video.payload.variants ?? [])].sort((a, b) => a.height - b.height);
      const out: { name: string; have: number; total: number }[] = [];
      for (const variant of variants) {
        if (!cached.has(variant.blob)) {
          out.push({ name: variant.name, have: 0, total: 0 });
          continue;
        }
        const plan = await planFor(video, variant.height);
        out.push({
          name: variant.name,
          have: plan.reduce((n, uuid) => n + (cached.has(uuid) ? 1 : 0), 0),
          total: plan.length,
        });
      }
      return out;
    },
    [cached, planFor],
  );

  /**
   * Watch for content added elsewhere — another device, or the CLI mid-upload.
   *
   * Polls the usage endpoint and only re-reads the library when the item count
   * or the newest timestamp has actually moved. Re-hydrating means decrypting
   * every payload, so doing it on a timer regardless would burn CPU to
   * discover nothing had happened.
   */
  useEffect(() => {
    if (state.status !== 'unlocked') return;
    let live = true;
    let seen: string | null = null;

    const check = async () => {
      if (!live || document.visibilityState !== 'visible') return;
      try {
        const usage = await api.usage();
        const signature = `${usage.item_count}:${usage.last_modified ?? ''}`;
        if (seen === null) {
          seen = signature;
          return;
        }
        if (signature !== seen) {
          seen = signature;
          await reload();
        }
      } catch {
        // Offline, or the session lapsed. The next tick tries again.
      }
    };

    void check();
    const timer = setInterval(() => void check(), POLL_MS);
    const onVisible = () => void check();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      live = false;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [state.status, reload]);

  const evict = useCallback(
    async (video: Item<VideoPayload>) => {
      await deleteBlobs(video.payload.blobs ?? []);
      await refreshCache();
    },
    [refreshCache],
  );

  const preload = useCallback(
    (video: Item<VideoPayload>, height: number) => {
      const uuid = video.uuid;
      if (preloadControllers.current.has(uuid)) return; // already in flight
      if ((video.payload.blobs ?? []).length === 0) return;

      const controller = new AbortController();
      preloadControllers.current.set(uuid, controller);
      setPreloads((p) => ({
        ...p,
        [uuid]: { done: 0, total: 0, bytes: 0, running: true, error: null },
      }));

      void (async () => {
      const blobs = await blobsToWarm(video, height, controller.signal);
      if (controller.signal.aborted) return;
      setPreloads((p) => ({
        ...p,
        [uuid]: { done: 0, total: blobs.length, bytes: 0, running: true, error: null },
      }));

      // A shared cursor rather than fixed slices: segments vary in size, and
      // this keeps every worker busy instead of waiting on the slowest chunk.
      let next = 0;
      let done = 0;
      let bytes = 0;
      let failed: string | null = null;

      const worker = async () => {
        while (next < blobs.length && !controller.signal.aborted) {
          const index = next++;
          try {
            const result = await api.warmBlob(blobs[index], controller.signal);
            bytes += result.bytes;
            // Reflect each blob the moment it lands, so the percentage climbs
            // during the download instead of jumping at the end.
            if (result.stored) {
              const landed = blobs[index];
              setCached((prev) => {
                if (prev.has(landed)) return prev;
                const next = new Set(prev);
                next.add(landed);
                return next;
              });
            }
          } catch (err) {
            if (controller.signal.aborted) return;
            // Keep going: one dead blob should not abandon the other forty.
            failed = err instanceof Error ? err.message : String(err);
          }
          done += 1;
          setPreloads((p) => ({
            ...p,
            [uuid]: { done, total: blobs.length, bytes, running: true, error: failed },
          }));
        }
      };

      await Promise.all(
        Array.from({ length: Math.min(PRELOAD_CONCURRENCY, blobs.length) }, worker),
      ).finally(() => {
        preloadControllers.current.delete(uuid);
        setPreloads((p) => ({
          ...p,
          [uuid]: { done, total: blobs.length, bytes, running: false, error: failed },
        }));
        // One rung per video: drop any other rung's segments now that this
        // one is here. Preloading 360p and later 480p would otherwise keep
        // both, quietly doubling what the video costs on disk.
        void (async () => {
          // Any single-rung target, which is now every target except ALL.
          if (!controller.signal.aborted && height !== PRELOAD_ALL) {
            const keep = new Set(blobs);
            const stale = (video.payload.blobs ?? []).filter((u) => !keep.has(u));
            if (stale.length) await deleteBlobs(stale);
          }
          await refreshCache();
        })();
      });
      })();
    },
    [blobsToWarm, refreshCache],
  );

  // Abort anything in flight when the vault locks or the tab unmounts.
  useEffect(() => {
    const controllers = preloadControllers.current;
    return () => {
      for (const c of controllers.values()) c.abort();
      controllers.clear();
    };
  }, []);

  const thumbUrl = useCallback(async (video: Item<VideoPayload>) => {
    const keys = keysRef.current;
    const blob = video.payload.thumb_blob;
    if (!keys || !blob) return null;
    const cached = thumbCache.current.get(blob);
    if (cached) return cached;
    try {
      const plain = await unseal(keys.meta, await api.getBlob(blob));
      const url = URL.createObjectURL(new Blob([plain.slice().buffer as ArrayBuffer], { type: 'image/jpeg' }));
      thumbCache.current.set(blob, url);
      return url;
    } catch {
      return null;
    }
  }, []);

  /**
   * One decrypted slide, as an object URL.
   *
   * Unlike a thumbnail this is not remembered here: a set can be hundreds of
   * full-size images, and holding them all decrypted would defeat the point of
   * the vault as thoroughly as writing them to disk would. The caller keeps a
   * small window and revokes what it no longer shows.
   *
   * The ciphertext stays in the blob cache, so stepping back to a slide is a
   * decrypt rather than a download.
   */
  const slideUrl = useCallback(async (blob: string): Promise<string | null> => {
    const keys = keysRef.current;
    if (!keys || !blob) return null;
    try {
      const plain = await unseal(keys.meta, await api.getBlob(blob));
      // No declared type: the bytes are whatever the CLI encoded, and the
      // browser sniffs an image far more reliably than we can guess here.
      return URL.createObjectURL(new Blob([plain.slice().buffer as ArrayBuffer]));
    } catch {
      return null;
    }
  }, []);

  useEffect(() => revokeThumbs, [revokeThumbs]);

  const value = useMemo<VaultApi>(
    () => ({
      ...state,
      keys: keysRef.current,
      signIn,
      unlock,
      lock,
      signOut,
      reload,
      createFolder,
      renameFolder,
      deleteFolder,
      moveFolder,
      moveVideo,
      reorderVideo,
      saveSettings,
      thumbUrl,
      slideUrl,
      preload,
      cancelPreload,
      preloads,
      cached,
      itemCount,
      storage,
      clearAllCached,
      planFor,
      refreshCache,
      evict,
      cachedRungs,
      rungSegments,
      levelOrder,
      coverageOf,
    }),
    [
      state,
      signIn,
      unlock,
      lock,
      signOut,
      reload,
      createFolder,
      renameFolder,
      deleteFolder,
      moveFolder,
      moveVideo,
      reorderVideo,
      saveSettings,
      thumbUrl,
      slideUrl,
      preload,
      cancelPreload,
      preloads,
      cached,
      itemCount,
      storage,
      clearAllCached,
      planFor,
      refreshCache,
      evict,
      cachedRungs,
      rungSegments,
      levelOrder,
      coverageOf,
    ],
  );

  return <VaultContext.Provider value={value}>{children}</VaultContext.Provider>;
}
